import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { X, Anchor, Target, Clock, Feather, Headphones } from 'lucide-react';

const FIELDS = [
  { key: 'anchor', label: 'Anchor', icon: Anchor,
    hint: 'What already happens right before this? ("after I pour my coffee")' },
  { key: 'intention', label: 'Intention', icon: Target,
    hint: 'The consciousness you are cultivating — not the task' },
  { key: 'landing', label: 'Landing', icon: Feather,
    hint: 'How you close it. A breath, a word, a hand on your chest' },
];

/**
 * Designing a ritual: the admin card brings the title, description and any fields he set;
 * whatever he left blank is the member's own work — Anchor, Intention, Landing, Duration.
 */
export const RitualDesigner = ({ card, ritual, onSaved, onClose }) => {
  const editing = !!ritual;
  const base = ritual || card || {};
  const [form, setForm] = useState({
    title: base.title || '',
    description: base.description || '',
    anchor: base.anchor || '',
    intention: base.intention || '',
    landing: base.landing || '',
    duration_minutes: base.duration_minutes || '',
    suggested_window: base.suggested_window || 'any',
  });
  const [busy, setBusy] = useState(false);
  const custom = !card && !ritual?.card_id;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error('Give this ritual a name');
    setBusy(true);
    const payload = {
      ...form,
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
    };
    try {
      let saved;
      if (editing) {
        saved = (await axios.patch(`${API}/companion/rituals/mine/${ritual.id}`, payload)).data;
      } else {
        saved = (await axios.post(`${API}/companion/rituals/mine`,
          { ...payload, card_id: card?.id || null })).data;
      }
      toast.success(editing ? 'Ritual updated' : 'Added to your rituals');
      onSaved(saved, !editing);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that ritual');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                    bg-slate-950/80 backdrop-blur-sm p-0 sm:p-4"
         data-testid="ritual-designer">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                      border border-white/10 bg-[#0b1020] p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300/70">
              {editing ? 'Your ritual' : 'Design your ritual'}
            </p>
            <h2 className="text-base md:text-lg font-semibold text-white mt-1">
              {form.title || 'New ritual'}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                  data-testid="ritual-designer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {custom && (
          <label className="block space-y-1.5">
            <span className="text-xs text-white/50">Name</span>
            <input value={form.title} onChange={set('title')}
                   placeholder="Morning breathwork"
                   className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2
                              text-sm text-white placeholder-white/25 focus:outline-none
                              focus:border-amber-300/60"
                   data-testid="ritual-title-input" />
          </label>
        )}

        {base.description && !custom && (
          <p className="text-sm text-white/55 leading-relaxed">{base.description}</p>
        )}

        {base.audio_url && (
          <div className="space-y-1.5" data-testid="designer-audio">
            <span className="flex items-center gap-2 text-xs text-white/55">
              <Headphones className="w-3.5 h-3.5 text-amber-300/70" /> Comes with a guided recording
            </span>
            <audio controls src={base.audio_url} className="w-full h-9"
                   style={{ colorScheme: 'dark' }} />
          </div>
        )}

        {FIELDS.map(({ key, label, icon: Icon, hint }) => (
          <label key={key} className="block space-y-1.5">
            <span className="flex items-center gap-2 text-xs text-white/60">
              <Icon className="w-3.5 h-3.5 text-amber-300/70" /> {label}
            </span>
            <input value={form[key]} onChange={set(key)} placeholder={hint}
                   className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2
                              text-sm text-white placeholder-white/25 focus:outline-none
                              focus:border-amber-300/60"
                   data-testid={`ritual-${key}-input`} />
          </label>
        ))}

        <label className="block space-y-1.5">
          <span className="flex items-center gap-2 text-xs text-white/60">
            <Clock className="w-3.5 h-3.5 text-amber-300/70" /> Duration — minimum viable
          </span>
          <input type="number" min="1" max="600" value={form.duration_minutes}
                 onChange={set('duration_minutes')} placeholder="Minutes (leave blank for open)"
                 className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2
                            text-sm text-white placeholder-white/25 focus:outline-none
                            focus:border-amber-300/60"
                 data-testid="ritual-duration-input" />
          <span className="text-[11px] text-white/35">
            With a duration you can log a partial sit. Without one it is simply done or not.
          </span>
        </label>

        <div className="flex gap-3 pt-1">
          <button onClick={save} disabled={busy}
                  className="flex-1 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold
                             text-slate-950 hover:bg-amber-300 disabled:opacity-50
                             transition-colors"
                  data-testid="ritual-designer-save">
            {editing ? 'Save' : 'Add to my rituals'}
          </button>
          <button onClick={onClose}
                  className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70
                             hover:text-white hover:border-white/30 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RitualDesigner;
